/**
 * Format date to YYYY-MM-DD string
 * @param {Date} date - Date to format
 * @returns {string|null} Formatted date string
 */
export const formatDateToISO = (date) => {
    if (!date) return null;
    return (
        date.getFullYear() +
        "-" +
        String(date.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(date.getDate()).padStart(2, "0")
    );
};

/**
 * Get default date range (3 months ago to today)
 * @returns {Object} Object with dateFrom and dateTo
 */
export const getDefaultDateRange = () => {
    const today = new Date();
    const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, 1);
    return {
        dateFrom: threeMonthsAgo,
        dateTo: today
    };
};

/**
 * Save dates to session storage
 * @param {string} keyFrom - Key for start date
 * @param {string} keyTo - Key for end date
 * @param {Date} dateFrom - Start date
 * @param {Date} dateTo - End date
 */
export const saveDatesToSession = (keyFrom, keyTo, dateFrom, dateTo) => {
    try {
        if (dateFrom) {
            sessionStorage.setItem(keyFrom, dateFrom.toISOString());
        }
        if (dateTo) {
            sessionStorage.setItem(keyTo, dateTo.toISOString());
        }
    } catch (error) {
        console.warn("Failed to save dates to session storage:", error);
    }
};

/**
 * Load dates from session storage
 * @param {string} keyFrom - Key for start date
 * @param {string} keyTo - Key for end date
 * @returns {Object|null} Object with dateFrom and dateTo, or null
 */
export const loadDatesFromSession = (keyFrom, keyTo) => {
    try {
        const savedDateFrom = sessionStorage.getItem(keyFrom);
        const savedDateTo = sessionStorage.getItem(keyTo);

        if (savedDateFrom && savedDateTo) {
            return {
                dateFrom: new Date(savedDateFrom),
                dateTo: new Date(savedDateTo)
            };
        }
    } catch (error) {
        console.warn("Failed to load dates from session storage:", error);
    }
    return null;
};

/**
 * Add hours to a date string and format to DD/MM/YYYY HH:mm
 * @param {string} dateTimeString - ISO date string from backend
 * @param {number} hoursToAdd - Hours to add (default: 4)
 * @returns {string} Formatted date string in DD/MM/YYYY HH:mm format
 */
export const formatDateTimeWithOffset = (dateTimeString, hoursToAdd = 4) => {
    if (!dateTimeString) return '';
    
    try {
        const date = new Date(dateTimeString);
        // Add hours to adjust for timezone
        date.setHours(date.getHours() + hoursToAdd);
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateTimeString;
    }
};

/**
 * Format date to DD/MM/YYYY (without time and without timezone offset)
 * @param {string} dateString - ISO date string from backend
 * @returns {string} Formatted date string in DD/MM/YYYY format
 */
export const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
        const date = new Date(dateString);
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
};

/**
 * Format time with offset (HH:mm)
 * @param {string} dateTimeString - ISO date string from backend
 * @param {number} hoursToAdd - Hours to add (default: 4)
 * @returns {string} Formatted time string in HH:mm format
 */
export const formatTimeWithOffset = (dateTimeString, hoursToAdd = 4) => {
    if (!dateTimeString) return '';
    
    try {
        const date = new Date(dateTimeString);
        // Add hours to adjust for timezone
        date.setHours(date.getHours() + hoursToAdd);
        
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${hours}:${minutes}`;
    } catch (error) {
        console.error('Error formatting time:', error);
        return dateTimeString.split('T')[1]?.substring(0, 5) || '';
    }
};

/**
 * Format time without offset (HH:mm) - for scheduled times
 * @param {string} dateTimeString - ISO date string from backend
 * @returns {string} Formatted time string in HH:mm format
 */
export const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    
    try {
        // Extract time directly from ISO string without timezone adjustment
        return dateTimeString.split('T')[1]?.substring(0, 5) || '';
    } catch (error) {
        console.error('Error formatting time:', error);
        return '';
    }
};

